import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2 } from "lucide-react";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
import ComplianceProofButton from "../../design-system/compliance/ComplianceProofButton";

const TYPES = ["License", "Insurance", "W-9", "COI", "OSHA", "Bond"];
const STATUSES = ["Verified", "Pending", "Expired"];

export default function CredentialsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "License", label: "", owner: "", status: "Pending", issued_at: "", expires_at: "" });
  const [attachId, setAttachId] = useState(null);
  const [attachReload, setAttachReload] = useState(0);

  const load = async () => setItems((await api.get("/credentials")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.issued_at = form.issued_at ? new Date(form.issued_at).toISOString() : null;
      payload.expires_at = form.expires_at ? new Date(form.expires_at).toISOString() : null;
      await api.post("/credentials", payload);
      toast.success("Credential added");
      setOpen(false);
      setForm({ type: "License", label: "", owner: "", status: "Pending", issued_at: "", expires_at: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/credentials/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell
      title="License & Compliance Credentials"
      subtitle={`${items.length} credential(s) tracked`}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="credentials-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add credential
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-erf-cream">
            <DialogHeader><DialogTitle className="font-display">New credential</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-nav">Type</Label>
                <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Label</Label>
                <Input required value={form.label} onChange={(e)=>setForm({...form, label: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Owner / holder</Label>
                <Input required value={form.owner} onChange={(e)=>setForm({...form, owner: e.target.value})} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-nav">Issued</Label>
                  <Input type="date" value={form.issued_at} onChange={(e)=>setForm({...form, issued_at: e.target.value})} className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Expires</Label>
                  <Input type="date" value={form.expires_at} onChange={(e)=>setForm({...form, expires_at: e.target.value})} className="mt-1 font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Status</Label>
                <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="credentials-submit">Add credential</Button>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {items.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No credentials tracked" subtitle="Add licenses, insurance, W-9, COI, OSHA, and bonds to enable expiration monitoring on the Owner Command Center." />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 mono">Expires</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-erf-borderCream/60" data-testid={`credential-row-${c.id}`}>
                  <td className="px-3 py-2 mono">{c.type}</td>
                  <td className="px-3 py-2 font-nav">{c.label}</td>
                  <td className="px-3 py-2">{c.owner}</td>
                  <td className="px-3 py-2"><StatusChip status={c.status === "Verified" ? "verified" : c.status === "Expired" ? "expired" : "pending"} /></td>
                  <td className="px-3 py-2 mono text-[11px]">{c.expires_at ? c.expires_at.slice(0,10) : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setAttachId(attachId === c.id ? null : c.id)} data-testid={`credential-attach-${c.id}`}>Docs</Button>
                    <Button size="sm" variant="ghost" className="text-red-700" onClick={() => del(c.id)} data-testid={`credential-delete-${c.id}`}><Trash2 className="w-4 h-4"/></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {attachId && (
        <div className="mt-4 bg-white border border-erf-borderCream rounded-md p-4" data-testid="credential-attach-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-nav text-erf-dark/60">Evidence for credential {attachId.slice(0, 8)}</div>
            <div className="flex items-center gap-2">
              <ComplianceProofButton recordType="credential" recordId={attachId} />
              <InformationIntake
                recordType="credential" recordId={attachId}
                category="insurance_gl" confidentiality="restricted"
                capabilities={["upload", "multi", "scan", "photo", "existing", "request_info"]}
                onChanged={() => setAttachReload((k) => k + 1)}
              />
            </div>
          </div>
          <AttachmentsPanel recordType="credential" recordId={attachId} reload={attachReload} />
        </div>
      )}
    </AppShell>
  );
}
